import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * Move stock of a single inventory item from its location to another.
 *
 * Props:
 *   open, onOpenChange
 *   item — { item_id, name, sku, stock_quantity, location_id }
 *   storeLocations — [{ location_id, name, code? }]
 *   onTransferred(result) — refresh parent
 */
export default function TransferStockModal({
  open,
  onOpenChange,
  item,
  storeLocations = [],
  onTransferred,
}) {
  const [targetId, setTargetId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetId("");
      setQuantity(1);
      setNote("");
    }
  }, [open, item?.item_id]);

  const close = () => {
    onOpenChange(false);
    setTimeout(() => setLoading(false), 250);
  };

  const available = Number(item?.stock_quantity ?? 0);
  const validQty = Number.isInteger(quantity) && quantity > 0 && quantity <= available;

  const eligibleTargets = (storeLocations || []).filter(
    (l) => l.location_id && l.location_id !== item?.location_id,
  );

  const run = async () => {
    if (!targetId) return toast.error("Choose a target location");
    if (!validQty) return toast.error(`Quantity must be 1–${available}`);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/inventory/transfer-stock`,
        {
          source_item_id: item.item_id,
          target_location_id: targetId,
          quantity,
          note: note.trim() || null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      toast.success(
        `Moved ${data.quantity} unit${data.quantity === 1 ? "" : "s"} — ${data.source_remaining} remaining at source`,
      );
      if (typeof onTransferred === "function") onTransferred(data);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-md" data-testid="transfer-stock-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" /> Transfer Stock
          </DialogTitle>
          <DialogDescription>
            Move units of <span className="font-medium text-foreground">{item?.name || item?.sku}</span> to another location.
            {item?.sku && <span className="text-muted-foreground"> SKU: <span className="font-mono">{item.sku}</span></span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              To location <span className="text-red-500">*</span>
            </label>
            <Select value={targetId || "_none_"} onValueChange={(v) => setTargetId(v === "_none_" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="transfer-target-select">
                <SelectValue placeholder="Choose target location…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">— Choose target —</SelectItem>
                {eligibleTargets.map((loc) => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}{loc.code ? ` (${loc.code})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Quantity <span className="text-muted-foreground">(available: {available})</span>
            </label>
            <Input
              type="number"
              min={1}
              max={available}
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value || "0", 10) || 0)}
              className="h-9"
              data-testid="transfer-qty-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Note <span className="font-normal">(optional)</span>
            </label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason or reference…"
              className="text-sm"
              data-testid="transfer-note-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={loading}>Cancel</Button>
          <Button
            className="btn-primary"
            onClick={run}
            disabled={loading || !targetId || !validQty}
            data-testid="transfer-execute-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
